import { useState, useCallback } from "react";
import axios, { AxiosResponse, AxiosRequestConfig } from "axios";
import { apiV1Endpoint } from "../../../../api/v1/endpoints";
import { message } from "antd";

interface OwnerResponse {
  success: boolean;
  message?: string;
  result?: any;
}

export const useCreateOwner = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createOwner = useCallback(async (values: any, ownerId?: String) => {
    setLoading(true);
    setError(null);
    try {
      // Normalize nationality and beneficiaries
      const normalizedNationality =
        typeof values.nationality === "object" ? values.nationality.value : values.nationality;

      const normalizedBeneficiaries = values.beneficiaries?.map(({ beneficiaryBank, ...rest }) => ({
        ...rest,
        beneficiaryBank: beneficiaryBank?.value,
      }));

      // 🧩 Build base payload
      const payload: Record<string, any> = {
        name: values.ownerName,
        identityType: values.identityType,
        nationality: normalizedNationality,
        city: values.ownerCity,
        email: values.ownerEmail,
        number: values.ownerNo,
        phone: values.ownerPhone,
        beneficiaries: normalizedBeneficiaries,
      };

      //  Add/remove fields based on identityType
      if (values.identityType === "company") {
        payload.companyName = values.companyName;
        payload.address = values.officeAddress;
        payload.poBox = values.poBox;
        payload.tradeLicenseNumber = values.licenseNo;
        payload.trnNumber = values.trnNumber;
      } else {
        payload.address = values.address;
      }

      Object.assign(payload, {
        ...(values.pocName && { pocName: values.pocName }),
        ...(values.pocPhone && { pocPhone: values.pocPhone }),
        ...(values?.pocWorkPhone && { pocWorkPhone: values?.pocWorkPhone }),
        ...(values.pocEmail && { pocEmail: values.pocEmail }),
        ...(values.note && { note: values.note }),
        ...(values.workPhone && { workPhone: values.workPhone }),
        ...(values.userId && { userId: values.userId }),
      });

      // 🛠️ Determine request method and URL
      const url = ownerId ? `${apiV1Endpoint}/property/owner/${ownerId}` : `${apiV1Endpoint}/property/owner`;

      const method = ownerId ? "patch" : "post";

      // 📡 Make API request dynamically
      const response: AxiosResponse<OwnerResponse> = await axios[method](url, payload);
      if (response?.status === 201) {
        message.success(response.data?.message || "Owner data has been added successfully");
        return response?.status;
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Failed to create owner");
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, error, createOwner };
};
